import { helpLinesScale } from './HelpLinesSystem';
import { getLineLength, toCoreVector } from './HelpLinesUtils';
import { IVector3 } from '@inter-ikea-betrakta/viewer';
const getDistance = (start, end) => {
    return getLineLength(toCoreVector(start, helpLinesScale), toCoreVector(end, helpLinesScale));
};
const getPosition = (start, end) => {
    return {
        x: (start.x + end.x) * 0.5 * helpLinesScale,
        y: (start.y + end.y) * 0.5 * helpLinesScale,
        z: (start.z + end.z) * 0.5 * helpLinesScale
    };
};
export const getLabelPositions = (coreApi, scenePositions, shapeCenter, forceAllPointsVisible) => {
    const labelPositions = scenePositions.map(scenePosition => {
        const { start, end, hidden } = scenePosition;
        const position = getPosition(start, end);
        const distance = getDistance(start, end);
        const point = coreApi
            .getModules()
            .viewer()
            ?.worldCoordToCanvas(position.x, position.y, position.z);
        return {
            dim: 'height',
            x: point ? point.X : 0,
            y: point ? point.Y : 0,
            distance: distance,
            visible: scenePosition.facingCamera && (!hidden || forceAllPointsVisible),
            rotation: getRotation(start, end, coreApi, shapeCenter)
        };
    });
    return labelPositions;
};
function getCanvasPosition(coreApi, pos) {
    return coreApi.ipexViewer.project_world_position_to_canvas(pos.x, pos.y, pos.z);
}
export function getShapeCenter(scenePositions) {
    let xTot = 0;
    let yTot = 0;
    for (let i = 0; i < scenePositions.length; ++i) {
        xTot += scenePositions[i].start.x;
        yTot += scenePositions[i].start.y;
    }
    return { x: xTot /= scenePositions.length, y: yTot /= scenePositions.length, z: 0 };
}
export function getRotation(start, end, coreApi, shapeCenter) {
    let lineCenter = null;
    //Assuming 1 meter up in the center of the room for now
    const center = getCanvasPosition(coreApi, shapeCenter);
    const p1 = getCanvasPosition(coreApi, start);
    const p2 = getCanvasPosition(coreApi, end);
    if (center && p1 && p2) {
        lineCenter = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        const angle = getLabelAngle(p1, p2);
        const offset = getLabelOffset(lineCenter, angle, center);
        if (lineCenter) {
            return getLabelCSSTransform(offset, angle);
        }
    }
    else {
        // Fallback on calculating 3D center of line (may also be null if off screen)
        const fallBacklineCenter = IVector3.scale(IVector3.add(start, end), 1 / 2);
        lineCenter = getCanvasPosition(coreApi, fallBacklineCenter);
    }
    return undefined;
}
export function getLabelAngle(p1, p2) {
    let angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    // If the angle is larger than half circle, flip it to prevent upside down labels.
    if (Math.abs(angle) > Math.PI / 2) {
        angle += Math.PI;
    }
    /* If the angle is between 88 and 92 degrees (calculation done in radians),
      flip it to always have "vertical" labels be read bottom to top. */
    if (Math.abs(angle - Math.PI / 2) < 0.035) {
        angle += Math.PI;
    }
    return angle;
}
const LABEL_OFFSET = 20;
export function getLabelOffset(position, angle, origo) {
    // Create two perpendicular points, one on each side of the angle.
    const p1 = {
        x: Math.sin(angle) * LABEL_OFFSET + position.x,
        y: -Math.cos(angle) * LABEL_OFFSET + position.y
    };
    const p2 = {
        x: -Math.sin(angle) * LABEL_OFFSET + position.x,
        y: Math.cos(angle) * LABEL_OFFSET + position.y
    };
    // Select the point that is furthest away from the origo.
    const positionWithOffset = Math.hypot(p1.x - origo.x, p1.y - origo.y) > Math.hypot(p2.x - origo.x, p2.y - origo.y)
        ? p1
        : p2;
    return { x: positionWithOffset.x - position.x, y: positionWithOffset.y - position.y };
}
export function getLabelCSSTransform(offset, angle) {
    return `translate(${offset.x}px, ${offset.y}px) rotate(${angle}rad)`;
}
//# sourceMappingURL=HelpLinesLineLabelUtils.js.map